import { Route, Routes } from "react-router-dom";
import Dashboard from "./components/Dashboard";
import ErrorMessage from "./components/ErrorMessage";
import ErrorMessageQueue from "./components/ErrorMessageQueue";
import LOINCCodes from "./components/LOINCCodes";
import CodeSets from "./components/CodeSets";
import SNOMEDCodes from "./components/SNOMEDCodes";
import Conditions from "./components/Conditions";
import Facilities from "./components/Facilities";
import RulesTable from "./components/RulesTable";
import NotFound from "./components/NotFound";
import NotAuthorized from "./components/NotAuthorized";
import RulesScreen from "./components/RulesScreen";
import UnknownFacilitiesQueue from "./components/UnknownFacilitiesQueue";
import DiscardQueue from "./components/DiscardQueue";
import PublicReports from "./components/PublicReports";
import MasterQueue from "./components/MasterQueue";
import AOELoincCodes from "./components/AOELoincCodes";
import LOINCForm from "./components/LOINCForm";
import ReportingLoincCodes from "./components/ReportingLoincCodes";
import CodeSetNormalization from "./components/CodeSetNormalization";
import SnomedNormalization from "./components/SnomedNormalization";
import PerformingFacility from "./components/PerformingFacility";
import FacilityForm from "./components/FacilityForm";
import UserProfile from "./components/UserProfile";
import UserRoleManagement from "./components/UserRoleManagement";
import { useNavigate } from "react-router-dom";
import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { LoginCallback } from "@okta/okta-react";
import config from "./okta/config";
import RequiredAuth from "./components/RequiredAuth";
import RolesAndPermissionsManagement from "./components/RolesAndPermissionsManagement";
import Permissions from "./components/Permissions";
import {
  NBQH,
  NBDTH,
  NBRMH,
  NBRH,
  NBAH,
  NBSH,
} from "../src/util/component-codes";
import { useEffect, useContext } from "react";
import UserContext from "../src/context/user-context";
import Configurations from "./components/Configurations";
import Logs from "./components/Logs";
import DuplicateMessageQueue from "./components/DuplicateMessageQueue";
import EicrErrorMessageQueue from "./components/EicrErrorMessageQueue";
import PicklistValueConfigurations from "./components/PicklistValueConfigurations";
import EicrDiscardQueue from "./components/EicrDiscardQueue";
import EicrMasterQueue from "./components/EicrMasterQueue";
const oktaAuth = new OktaAuth(config.oidc);

const App = () => {
  const navigate = useNavigate();
  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || "/", window.location.origin));
  };
  const [user] = useContext(UserContext);

  const componentPermission = (componentCode, user, component) => {
    let components = user?.components;
    let hasAccess = components?.some(
      (item) => item?.componentCode === componentCode
    );
    if (hasAccess) {
      return component;
    } else {
      return <NotAuthorized />;
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
    script.type = "text/javascript";
    script.setAttribute(
      "data-domain-script",
      process.env.REACT_APP_COOKIE_SCRIPT
    );
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <Routes>
          <Route path="/" element={<RequiredAuth />}>
            <Route path="" element={<Dashboard />} />
            <Route
              path="/errorMessageQueue"
              element={componentPermission(NBQH, user, <ErrorMessageQueue />)}
            />
            <Route
              path="/discardQueue"
              element={componentPermission(NBQH, user, <DiscardQueue />)}
            />
            <Route
            path="/eicrDiscardQueue"
            element={componentPermission(NBQH, user, <EicrDiscardQueue />)}
            />
            <Route
              path="/error"
              element={componentPermission(NBQH, user, <ErrorMessage />)}
            />
            <Route
              path="/masterQueue"
              element={componentPermission(NBQH, user, <MasterQueue />)}
            />
            <Route
              path="/eicrMasterQueue"
              element={componentPermission(NBQH, user, <EicrMasterQueue />)}
            />
            <Route
              path="/unknownFacilities"
              element={componentPermission(
                NBQH,
                user,
                <UnknownFacilitiesQueue />
              )}
            />
            <Route
              path="/duplicateMessageQueue"
              element={componentPermission(
                NBQH,
                user,
                <DuplicateMessageQueue />
              )}
            />
            <Route
              path="/eicrErrorMessageQueue"
              element={componentPermission(
                NBQH,
                user,
                <EicrErrorMessageQueue />
              )}
            />
            <Route
              path="/LOINCCodes"
              element={componentPermission(NBDTH, user, <LOINCCodes />)}
            />
            <Route
              path="/AOELoincCodes"
              element={componentPermission(NBDTH, user, <AOELoincCodes />)}
            />
            <Route
              path="/reportingLoincCodes"
              element={componentPermission(
                NBDTH,
                user,
                <ReportingLoincCodes />
              )}
            />
            <Route
              path="/codeSets"
              element={componentPermission(NBDTH, user, <CodeSets />)}
            />
            <Route
              path="/SNOMEDCodes"
              element={componentPermission(NBDTH, user, <SNOMEDCodes />)}
            />
            <Route
              path="/facilities"
              element={componentPermission(NBDTH, user, <Facilities />)}
            />
            <Route
              path="/performingFacilities"
              element={componentPermission(NBDTH, user, <PerformingFacility />)}
            />
            <Route
              path="/conditions"
              element={componentPermission(NBDTH, user, <Conditions />)}
            />
            <Route
              path="/rules"
              element={componentPermission(NBRMH, user, <RulesTable />)}
            />
            <Route
              path="/rulesScreen"
              element={componentPermission(NBRMH, user, <RulesScreen />)}
            />
            <Route
              path="/loincForm"
              element={componentPermission(NBDTH, user, <LOINCForm />)}
            />
            <Route
              path="/facilityForm"
              element={componentPermission(NBDTH, user, <FacilityForm />)}
            />
            <Route
              path="/reporting"
              element={componentPermission(NBRH, user, <PublicReports />)}
            />
            <Route
              path="/codeSetNormalization"
              element={componentPermission(
                NBSH,
                user,
                <CodeSetNormalization />
              )}
            />
            <Route
              path="/snomedNormalization"
              element={componentPermission(
                NBSH,
                user,
                <SnomedNormalization />
              )}
            />
            <Route
              path="/configurations"
              element={componentPermission(NBSH, user, <Configurations />)}
            />
                        <Route
              path="/picklistValueConfiguration"
              element={componentPermission(NBSH, user, <PicklistValueConfigurations />)}
            />


            <Route path="/userProfile" element={<UserProfile />} />
            <Route
              path="/userRoleManagement"
              element={componentPermission(NBAH, user, <UserRoleManagement />)}
            />
            <Route
              path="/rolesAndPermissions"
              element={componentPermission(
                NBAH,
                user,
                <RolesAndPermissionsManagement />
              )}
            />
            <Route
              path="/logs"
              element={componentPermission(NBAH, user, <Logs />)}
            />
            <Route
              path="/permissions"
              element={componentPermission(NBAH, user, <Permissions />)}
            />
            <Route path="/logout" />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="*" element={<NotFound />} />
          <Route path="login/callback" element={<LoginCallback />} />
        </Routes>
      </Security>
    </>
  );
};

export default App;